import { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import axios from "axios"
import { Video } from "lucide-react"
import { PageSEO } from "@/components/page-seo"
import { Button } from "@/components/ui/button"
import { API_BASE } from "@/config/api"
import { formatRon, formatDate } from "@/lib/format"

const API = API_BASE

function isTelehealthLocation(location) {
  const loc = (location || "").toLowerCase()
  return loc.includes("telehealth") || loc.includes("telemedicin") || loc.includes("online")
}

export default function PatientPortal() {
  const { t } = useTranslation()
  const [params] = useSearchParams()
  const token = params.get("token") || ""
  const [session, setSession] = useState(null)
  const [invoices, setInvoices] = useState([])
  const [appointments, setAppointments] = useState([])
  const [documents, setDocuments] = useState({ care_plans: [], attachments: [] })
  const [err, setErr] = useState("")

  useEffect(() => {
    if (!token) {
      setErr(t("public.patientPortal.missingToken"))
      return
    }
    const q = { params: { token } }
    axios
      .get(`${API}/public/patient/session`, q)
      .then((r) => {
        setSession(r.data)
        return Promise.all([
          axios.get(`${API}/public/patient/invoices`, q),
          axios.get(`${API}/public/patient/appointments`, q),
          axios.get(`${API}/public/patient/documents`, q),
        ])
      })
      .then(([inv, appt, docs]) => {
        setInvoices(inv.data.invoices || [])
        setAppointments(appt.data.appointments || [])
        setDocuments(docs.data || { care_plans: [], attachments: [] })
      })
      .catch(() => setErr(t("public.patientPortal.invalidLink")))
  }, [token, t])

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <PageSEO seoKey="patientPortal" />
        <p className="text-destructive text-sm">{err}</p>
      </div>
    )
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-background">
        <PageSEO seoKey="patientPortal" />
        <p className="text-muted-foreground text-sm">{t("public.patientPortal.loading")}</p>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background p-6 md:p-10">
      <PageSEO seoKey="patientPortal" />
      <div className="max-w-lg mx-auto rounded-2xl border border-border bg-card p-8 space-y-8">
        <div>
          <h1 className="font-display text-2xl tracking-tight font-medium">{session.practice_name}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("public.patientPortal.portalSubtitle", { name: session.client_name })}
          </p>
          <p className="text-xs text-muted-foreground mt-4">{t("public.patientPortal.disclaimer")}</p>
        </div>

        <section>
          <h2 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t("public.patientPortal.upcomingAppointments")}</h2>
          <ul className="divide-y divide-border text-sm">
            {appointments.map((a) => (
              <li key={a.id} className="py-3 flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center">
                <div>
                  <span>{a.type}</span>
                  <span className="text-muted-foreground sm:ml-3 block sm:inline">{formatDate(a.start)}</span>
                </div>
                {isTelehealthLocation(a.location) && a.meeting_url && (
                  <Button size="sm" variant="outline" className="rounded-full shrink-0" asChild>
                    <Link to={`/patient/call?token=${encodeURIComponent(token)}&appointment_id=${a.id}`}>
                      <Video className="h-3.5 w-3.5 mr-1" />
                      {t("public.telehealth.joinConsultation")}
                    </Link>
                  </Button>
                )}
              </li>
            ))}
            {!appointments.length && <li className="py-3 text-muted-foreground">{t("public.patientPortal.noAppointments")}</li>}
          </ul>
        </section>

        <section>
          <h2 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">{t("public.patientPortal.documents")}</h2>
          <ul className="text-sm space-y-2">
            {(documents.care_plans || []).map((p) => (
              <li key={p.id} className="rounded-lg border border-border px-3 py-2">{t("public.patientPortal.planLabel", { title: p.title })}</li>
            ))}
            {(documents.attachments || []).map((a) => (
              <li key={a.id}>
                <a href={a.url} className="text-accent underline" target="_blank" rel="noreferrer">{a.filename}</a>
              </li>
            ))}
            {!documents.care_plans?.length && !documents.attachments?.length && (
              <li className="text-muted-foreground">{t("public.patientPortal.noDocuments")}</li>
            )}
          </ul>
        </section>

        <section>
          <h2 className="text-xs uppercase tracking-wider font-semibold text-muted-foreground mb-3">
            {t("public.patientPortal.invoicesTitle")}
          </h2>
          <ul className="divide-y divide-border text-sm">
            {invoices.map((inv) => (
              <li key={inv.id} className="py-3 flex justify-between gap-4 flex-wrap">
                <span className="font-mono text-xs">{inv.number}</span>
                <span className="text-muted-foreground">{formatDate(inv.issued_at)}</span>
                <span className="font-medium">{formatRon(inv.amount)}</span>
                <span className="text-xs capitalize">{inv.status}</span>
              </li>
            ))}
            {!invoices.length && (
              <li className="py-4 text-muted-foreground">{t("public.patientPortal.noInvoices")}</li>
            )}
          </ul>
        </section>
      </div>
    </div>
  )
}
